import { Component, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { RouteDirectionsRes } from '@features/map/interfaces/route-planning-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-elevation-profile',
  imports: [NgxEchartsDirective, TranslatePipe],
  templateUrl: './elevation-profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./elevation-profile.component.scss'],
})
export class ElevationProfileComponent {
  private translate = inject(TranslateService);
  routeData = input.required<RouteDirectionsRes>();
  closed = output<void>();

  summary = computed(() => this.routeData().Summary);
  histogram = computed(() => this.routeData().ElevationHistogram);

  chartOptions = computed((): EChartsOption => {
    const bins = this.histogram().Bins;
    const data = bins
      .filter((bin) => bin.AvgAltitude != null)
      .map((bin) => [
        parseFloat((bin.StartDistanceMeters / 1000).toFixed(2)),
        parseFloat(bin.AvgAltitude!.toFixed(1)),
      ]);

    const hasData = data.length > 0;

    const minAltitude = hasData ? Math.floor(Math.min(...data.map(([, alt]) => alt)) / 50) * 50 : 0;
    const maxDistance = hasData
      ? parseFloat((this.histogram().TotalDistanceMeters / 1000).toFixed(2))
      : 10;

    return {
      grid: { top: 20, bottom: 30, left: 50, right: 20 },
      xAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => `${v} km` },
        splitLine: { lineStyle: { type: 'dashed', color: '#e0e0e0' } },
        min: 0,
        max: maxDistance,
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => `${v} m` },
        splitLine: { lineStyle: { type: 'dashed', color: '#e0e0e0' } },
        min: hasData ? minAltitude : 0,
        max: hasData ? undefined : 500,
      },
      graphic: !hasData
        ? [
            {
              type: 'text',
              left: 'center',
              top: 'middle',
              style: {
                text: this.translate.instant('ElevationProfileDiagram.NoData'),
                font: '14px sans-serif',
                fill: '#9ca3af',
              },
            },
          ]
        : [],
      series: [
        {
          type: 'line',
          data,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: hasData ? '#1a73e8' : 'transparent', width: 2 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: hasData ? 'rgba(26,115,232,0.3)' : 'transparent' },
                { offset: 1, color: 'transparent' },
              ],
            },
          },
        },
      ],
      tooltip: {
        trigger: hasData ? 'axis' : 'none',
        backgroundColor: '#fff',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        padding: [8, 12],
        textStyle: {
          color: '#222',
          fontSize: 13,
        },
        formatter: (params: any) => {
          const [km, alt] = params[0].data;
          return `
            <div style="font-weight:600;margin-bottom:4px">${this.translate.instant('ElevationProfileDiagram.Distance')}: ${km} km</div>
            <div style="color:#1a73e8;font-weight:600">${this.translate.instant('ElevationProfileDiagram.Height')}: ${alt} m</div>
    `;
        },
      },
    };
  });

  close(): void {
    this.closed.emit();
  }
}
